import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../api";
import ContactTable from "../components/ContactTable";
import UploadContacts from "../components/UploadContacts";

const fetchContacts = async ({ queryKey }) => {
  const [, params] = queryKey;
  const { page = 1, limit = 10, q = "" } = params || {};
  const qs = `?page=${page}&limit=${limit}${q ? `&q=${encodeURIComponent(q)}` : ""}`;
  const res = await api.get(`/contacts${qs}`);
  return res.data;
};

export default function ContactsPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ["contacts", { page, limit, q: debouncedSearch }],
    queryFn: fetchContacts,
    keepPreviousData: true
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const contacts = data?.data || [];
  const pagination = data?.pagination || {};

  return (
    <div className="yc-contacts-page">
      {/* Upload Section */}
      <div className="yc-ct-upload-card">
        <div className="yc-ct-card-header">
          <span className="yc-ct-card-icon">📤</span>
          <span>Upload Contacts</span>
        </div>
        <div className="yc-ct-card-body">
          <UploadContacts />
        </div>
      </div>

      {/* Contacts List Section */}
      <div className="yc-ct-list-card">
        <div className="yc-ct-card-header">
          <div className="yc-ct-header-left">
            <span className="yc-ct-card-icon">👥</span>
            <span>Contacts List</span>
            <span className="yc-ct-total-badge">
              {pagination.totalDocs ?? "—"} contacts
            </span>
          </div>
          {isFetching && <span className="yc-ct-updating">Updating...</span>}
        </div>

        {/* Search & Controls Bar */}
        <div className="yc-ct-toolbar">
          <div className="yc-ct-search-wrap">
            <svg className="yc-ct-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="yc-ct-search"
              placeholder="Search by name, phone, or group..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="yc-ct-search-clear" onClick={() => setSearch("")}>✕</button>
            )}
          </div>
          <div className="yc-ct-per-page">
            <label>Show</label>
            <div className="yc-ct-select-wrap">
              <select
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <svg className="yc-ct-select-arrow" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
            </div>
            <label>per page</label>
          </div>
        </div>

        {/* Table */}
        <div className="yc-ct-table-wrap">
          {isLoading ? (
            <div className="yc-ct-status">Loading contacts...</div>
          ) : isError ? (
            <div className="yc-ct-status yc-ct-status--error">Error loading contacts</div>
          ) : (
            <>
              <ContactTable contacts={contacts} />

              {/* Pagination */}
              <div className="yc-ct-pagination">
                <div className="yc-ct-page-info">
                  Page <strong>{pagination.page || page}</strong> of <strong>{pagination.totalPages || "?"}</strong>
                </div>
                <div className="yc-ct-page-btns">
                  <button
                    className="yc-ct-page-btn"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || isFetching}
                  >
                    ← Previous
                  </button>
                  <button
                    className="yc-ct-page-btn"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={pagination.hasNextPage === false || isFetching}
                  >
                    Next →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
